import { makeAutoObservable, runInAction } from 'mobx';
import { dwollaApi } from '../api/dwolla_api';
import type { DwollaStatus, DwollaSource, DwollaTransaction } from '../types';

export class DwollaStore {
  status: DwollaStatus | null = null;
  sources: DwollaSource[] = [];
  transactions: DwollaTransaction[] = [];

  loading = false;
  loadingSources = false;
  loadingTransactions = false;
  saving = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchStatus(): Promise<void> {
    if (this.loading) return;

    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const status = await dwollaApi.getStatus();
      runInAction(() => {
        this.status = status;
        this.loading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load Dwolla status';
      });
    }
  }

  async createCustomer(firstName: string, lastName: string, email: string): Promise<boolean> {
    runInAction(() => {
      this.saving = true;
      this.error = null;
    });

    try {
      const result = await dwollaApi.createCustomer({
        first_name: firstName,
        last_name: lastName,
        email: email,
      });
      runInAction(() => {
        this.status = {
          has_customer: result.has_customer,
          has_funding_source: result.has_funding_source,
        };
        this.saving = false;
      });
      return true;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.error = err instanceof Error ? err.message : 'Failed to create Dwolla customer';
      });
      return false;
    }
  }

  async fetchSources(): Promise<void> {
    if (this.loadingSources) return;

    runInAction(() => {
      this.loadingSources = true;
      this.error = null;
    });

    try {
      const result = await dwollaApi.getSources();
      runInAction(() => {
        this.sources = result.sources;
        this.loadingSources = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loadingSources = false;
        this.error = err instanceof Error ? err.message : 'Failed to load funding sources';
      });
    }
  }

  async addFundingSource(
    routingNumber: string,
    accountNumber: string,
    accountType: 'checking' | 'savings',
    name?: string
  ): Promise<boolean> {
    runInAction(() => {
      this.saving = true;
      this.error = null;
    });

    try {
      const result = await dwollaApi.addFundingSource({
        routing_number: routingNumber,
        account_number: accountNumber,
        account_type: accountType,
        name: name,
      });
      runInAction(() => {
        if (this.status) {
          this.status.has_funding_source = result.has_funding_source;
        }
        this.saving = false;
      });
      // Refresh sources list
      this.fetchSources();
      return true;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.error = err instanceof Error ? err.message : 'Failed to add bank account';
      });
      return false;
    }
  }

  async removeSource(fundingLocation?: string): Promise<boolean> {
    runInAction(() => {
      this.saving = true;
      this.error = null;
    });

    try {
      await dwollaApi.removeSource(fundingLocation);
      runInAction(() => {
        this.saving = false;
      });
      // Refresh sources and status
      await Promise.all([this.fetchSources(), this.fetchStatus()]);
      return true;
    } catch (err) {
      runInAction(() => {
        this.saving = false;
        this.error = err instanceof Error ? err.message : 'Failed to remove funding source';
      });
      return false;
    }
  }

  async fetchTransactions(): Promise<void> {
    if (this.loadingTransactions) return;

    runInAction(() => {
      this.loadingTransactions = true;
      this.error = null;
    });

    try {
      const result = await dwollaApi.getTransactions();
      runInAction(() => {
        this.transactions = result.transactions;
        this.loadingTransactions = false;
      });
    } catch (err) {
      runInAction(() => {
        this.loadingTransactions = false;
        this.error = err instanceof Error ? err.message : 'Failed to load transactions';
      });
    }
  }

  clearError(): void {
    this.error = null;
  }

  reset(): void {
    this.status = null;
    this.sources = [];
    this.transactions = [];
    this.loading = false;
    this.loadingSources = false;
    this.loadingTransactions = false;
    this.saving = false;
    this.error = null;
  }
}

export const dwollaStore = new DwollaStore();
